// scheduling/components/distribution/ExtendToggles.jsx
// "Extend before / Extend after" checkboxes for the optimize flow. Looks back/
// forward up to 14 days, adds up to 5 eligible weekdays (Mon-Fri, plus Sat if
// the staffer has Saturday capacity), and re-runs the optimizer with the
// expanded date range. Bubbles the result up via onChange.
//
// Lifted from SchedulerGrid.jsx:4224-4311. Controlled component — caller owns
// overflowStart/overflowEnd state.

import { computeOptimizedHours } from '../../utils/optimizeHours';

function toISO(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }

function buildExpandedDates({ baseISOs, expandStart, expandEnd, staffId, getDayCap }) {
  let dates = [...baseISOs];
  if (expandStart && dates.length > 0) {
    const first = new Date(dates[0] + 'T12:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let added = 0;
    for (let i = 1; added < 5 && i <= 14; i++) {
      const prev = new Date(first); prev.setDate(first.getDate() - i);
      if (prev < today) break;
      const dow = prev.getDay();
      if (dow === 0) continue; // skip Sunday
      if (dow === 6 && getDayCap(staffId, toISO(prev)) <= 0) continue; // skip Sat without cap
      dates.unshift(toISO(prev)); added++;
    }
  }
  if (expandEnd && dates.length > 0) {
    const last = new Date(dates[dates.length - 1] + 'T12:00:00');
    let added = 0;
    for (let i = 1; added < 5 && i <= 14; i++) {
      const next = new Date(last); next.setDate(last.getDate() + i);
      const dow = next.getDay();
      if (dow === 0) continue;
      if (dow === 6 && getDayCap(staffId, toISO(next)) <= 0) continue;
      dates.push(toISO(next)); added++;
    }
  }
  return dates;
}

export default function ExtendToggles({
  staffId,
  candidateDates,             // ISO[] — current base range
  totalHours,                 // total to redistribute
  excludeEntryIds,            // Set<EntryId>
  getDayCap,
  entries,
  dayHoursByStaff,
  overflowStart,
  overflowEnd,
  onChange,                   // ({ overflowStart, overflowEnd, dailyHours, totalAssigned, expandedDates, warnings }) => void
  showOverCapWarning = true,  // toggle the orange hint when over-cap days exist but neither extend is on
  hasOverCap = false,         // caller computes this from current preview
}) {
  const handleToggle = (which, checked) => {
    const nextStart = which === 'start' ? checked : overflowStart;
    const nextEnd = which === 'end' ? checked : overflowEnd;
    const expandedDates = buildExpandedDates({
      baseISOs: candidateDates,
      expandStart: nextStart,
      expandEnd: nextEnd,
      staffId,
      getDayCap,
    });
    const result = computeOptimizedHours({
      staffId,
      candidateDates: expandedDates,
      totalHours,
      excludeEntryIds: excludeEntryIds || new Set(),
      getDayCapFn: getDayCap,
      entriesArr: entries,
      dayHoursMap: dayHoursByStaff,
    });
    onChange({
      overflowStart: nextStart,
      overflowEnd: nextEnd,
      dailyHours: result.dailyHours,
      totalAssigned: result.totalAssigned,
      expandedDates,
      warnings: result.warnings,
    });
  };

  return (
    <>
      <label style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 11, color: '#6B7280' }}>
        <input type="checkbox" checked={!!overflowStart}
          onChange={e => handleToggle('start', e.target.checked)}
          style={{ accentColor: '#003D4C' }} />
        Extend before
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 11, color: '#6B7280' }}>
        <input type="checkbox" checked={!!overflowEnd}
          onChange={e => handleToggle('end', e.target.checked)}
          style={{ accentColor: '#003D4C' }} />
        Extend after
      </label>
      {showOverCapWarning && hasOverCap && !overflowStart && !overflowEnd && (
        <span style={{ fontSize: 10, color: '#D97706', fontWeight: 600 }}>
          &#9888; Some days still exceed capacity &mdash; try extending the date range
        </span>
      )}
    </>
  );
}
